import type { FieldProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { object, string } from 'yup';

import ContentLoader from '@components/ContentLoader';
import MainTitle from '@components/MainTitle';
import RoundedButton from '@components/RoundedButton/index';
import Textarea from '@components/Textarea';

import { isDevelopment } from '@constants/env';

import {
  useSendDisableUserEmail,
  useSendEnableUserEmail,
} from '@hooks/api/notification';

import { RecordsUpdateStyle } from '../ReportContent/ReportContent.style';

const messages = defineMessages({
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  title: {
    id: 'disableEnableUser.title',
    defaultMessage: '{text}activation Request',
  },
  disableSubtitle: {
    id: 'disableEnableUser.disableSubtitle',
    defaultMessage:
      'Please provide a brief explanation of why you want to deactivate and/or cancel your profile. A representative will follow up with you within 24-48 hours.',
  },
  enableSubtitle: {
    id: 'disableEnableUser.enableSubtitle',
    defaultMessage:
      'Please notify a VITALL representative that I would like to reactivate my profile.',
  },
});

const DisableEnableUserContent: React.FC<{
  /**
   * Callback for when the submit button is clicked
   */
  onSubmit?: () => void;
  /**
   * User email
   */
  email?: string | null;
  /**
   * Is this modal used to disable or enable a user?
   */
  disabled: boolean;
  /**
   * Text of submit button
   */
  buttonText?: string;
}> = ({ disabled, onSubmit = () => null, email, buttonText }) => {
  const { formatMessage } = useIntl();

  const [sendDisableEmail, sendDisableEmailLoading] = useSendDisableUserEmail();
  const [sendEnableEmail, sendEnableEmailLoading] = useSendEnableUserEmail();
  const handleClose = () => onSubmit();

  const loading = sendDisableEmailLoading || sendEnableEmailLoading;

  const initialValues: {
    message: string;
    submit: string | null;
  } = {
    message: '',
    submit: null,
  };

  const validationSchema = object().shape({
    message: disabled
      ? string()
      : string().required(formatMessage(messages.required)),
  });

  return (
    <RecordsUpdateStyle>
      <MainTitle
        title={formatMessage(messages.title, {
          text: disabled ? 'Re' : 'De',
        })}
      />
      <p>
        {formatMessage(
          disabled ? messages.enableSubtitle : messages.disableSubtitle
        )}
      </p>

      <ContentLoader lineCount={1} loading={loading}>
        <Formik
          initialValues={initialValues}
          onSubmit={async (values, { setSubmitting }) => {
            if (!email) {
              return;
            }
            try {
              // If user is disabled, re-enable user
              if (disabled) {
                await sendEnableEmail({
                  input: {
                    email,
                  },
                });
              } else {
                await sendDisableEmail({
                  input: {
                    email,
                    message: values.message,
                  },
                });
              }
            } catch (e) {
              if (isDevelopment) {
                // eslint-disable-next-line no-console
                console.error(e);
              }
            } finally {
              handleClose();
              setSubmitting(false);
            }
          }}
          validationSchema={validationSchema}
        >
          {({ errors, isSubmitting }) => (
            <Form>
              {!disabled && (
                <Field name="message">
                  {({ field, meta }: FieldProps<string>) => (
                    <Textarea
                      message={meta.touched ? errors.message : ''}
                      {...field}
                    />
                  )}
                </Field>
              )}

              <RoundedButton
                color="error"
                disabled={isSubmitting || loading}
                margin={10}
                type="submit"
                width="150px"
              >
                {buttonText ?? formatMessage(messages.submit)}
              </RoundedButton>
            </Form>
          )}
        </Formik>
      </ContentLoader>
    </RecordsUpdateStyle>
  );
};

export default DisableEnableUserContent;
